import { useRef, useState } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Building2, Eye, EyeOff, Mail, Shield } from 'lucide-react';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import { useAuth } from '../../hooks/useAuth';
import { ENV } from '../../config/env';
import { loginWithUsernameOrEmail, verifyPortalLoginOtp } from '../../services/api';
import { isLoginOtpChallenge, type AgentLoginSuccessData } from '../../types';
import { toAgentProfile } from '../../utils/sanitizeUser';

interface CredentialsFormData {
  loginId: string;
  password: string;
}

interface OtpFormData {
  otp: string;
}

function profileFromLoginData(data: AgentLoginSuccessData) {
  const raw = data.superAgent || data.agent;
  return raw ? toAgentProfile(raw as Record<string, unknown>) : null;
}

export default function LoginPage() {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState<'credentials' | 'code'>('credentials');
  const [pendingToken, setPendingToken] = useState('');
  const [otpChannel, setOtpChannel] = useState<'email' | 'phone'>('email');
  const [maskedDestination, setMaskedDestination] = useState('');
  const turnstileRef = useRef<TurnstileInstance>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const captchaRequired = ENV.TURNSTILE_ENABLED && !!ENV.TURNSTILE_SITE_KEY;
  const from = (location.state as { from?: { pathname?: string } })?.from?.pathname || '/dashboard';

  const credentialsForm = useForm<CredentialsFormData>({
    defaultValues: { loginId: '', password: '' },
  });
  const otpForm = useForm<OtpFormData>({
    defaultValues: { otp: '' },
  });

  const finishLogin = (accessToken: string, data: AgentLoginSuccessData) => {
    const profile = profileFromLoginData(data);
    if (!profile) {
      toast.error('Sign-in failed');
      return;
    }
    login(profile, accessToken);
    toast.success('Signed in successfully');
    navigate(from, { replace: true });
  };

  const onCredentialsSubmit = async (data: CredentialsFormData) => {
    if (captchaRequired && !captchaToken) {
      toast.error('Please complete the CAPTCHA');
      return;
    }
    setSubmitting(true);
    try {
      const res = await loginWithUsernameOrEmail(
        data.loginId,
        data.password,
        captchaToken || undefined
      );
      if (!res.success || !res.data) {
        toast.error(res.message || 'Sign-in failed');
        turnstileRef.current?.reset();
        setCaptchaToken(null);
        return;
      }
      if (isLoginOtpChallenge(res.data)) {
        setPendingToken(res.data.pendingToken);
        setOtpChannel(res.data.channel);
        setMaskedDestination(res.data.maskedDestination || '');
        setStep('code');
        otpForm.setValue('otp', '');
        toast.success(res.message || 'Enter the verification code we sent you.');
        return;
      }
      if (!res.data.accessToken) {
        toast.error(res.message || 'Sign-in failed');
        return;
      }
      finishLogin(res.data.accessToken, res.data);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Invalid username/email or password';
      toast.error(msg);
      turnstileRef.current?.reset();
      setCaptchaToken(null);
    } finally {
      setSubmitting(false);
    }
  };

  const onOtpSubmit = async (data: OtpFormData) => {
    if (!pendingToken) return;
    setSubmitting(true);
    try {
      const res = await verifyPortalLoginOtp(pendingToken, data.otp.trim());
      if (!res.success || !res.data || isLoginOtpChallenge(res.data)) {
        toast.error(res.message || 'Invalid or expired code');
        otpForm.setValue('otp', '');
        return;
      }
      if (!res.data.accessToken) {
        toast.error(res.message || 'Sign-in failed');
        return;
      }
      finishLogin(res.data.accessToken, res.data);
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Invalid or expired code';
      toast.error(msg);
      otpForm.setValue('otp', '');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setPendingToken('');
    otpForm.setValue('otp', '');
  };

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-orange-50 via-white to-amber-50 px-3 py-6 sm:flex sm:items-center sm:justify-center sm:px-4 sm:py-10">
      <div className="mx-auto w-full max-w-md space-y-6 sm:space-y-8">
        <div className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-500 text-white shadow-lg">
            <Building2 className="h-8 w-8" />
          </div>
          <h1 className="mt-4 text-2xl font-bold text-gray-900">Super Agent portal</h1>
          <p className="mt-1 text-sm text-gray-600">Separate sign-in from the Admin system</p>
        </div>

        <div className="rounded-2xl border border-orange-100 bg-white p-5 shadow-xl shadow-orange-100/50 sm:p-8">
          <h2 className="text-lg font-semibold text-gray-900 text-center mb-2">Super Agent sign-in</h2>
          <p className="text-center text-sm text-gray-600 mb-6">
            {step === 'credentials'
              ? 'Username or email and password'
              : otpChannel === 'phone'
                ? 'Enter the code sent to your phone'
                : 'Enter the code sent to your email'}
          </p>

          {step === 'credentials' ? (
            <form onSubmit={credentialsForm.handleSubmit(onCredentialsSubmit)} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Username or email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    autoComplete="username"
                    className="w-full rounded-lg border border-gray-200 py-2.5 pl-10 pr-3 text-base focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
                    placeholder="superagent001 or superagent@example.com"
                    {...credentialsForm.register('loginId', { required: 'Username or email is required' })}
                  />
                </div>
                {credentialsForm.formState.errors.loginId && (
                  <p className="mt-1 text-sm text-red-600">{credentialsForm.formState.errors.loginId.message}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                <div className="relative">
                  <button
                    type="button"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPw(!showPw)}
                    aria-label="Show password"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <input
                    type={showPw ? 'text' : 'password'}
                    autoComplete="current-password"
                    className="w-full rounded-lg border border-gray-200 py-2.5 pl-3 pr-10 text-base focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
                    placeholder="••••••••"
                    {...credentialsForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 6, message: 'At least 6 characters' },
                    })}
                  />
                </div>
                {credentialsForm.formState.errors.password && (
                  <p className="mt-1 text-sm text-red-600">{credentialsForm.formState.errors.password.message}</p>
                )}
              </div>
              {captchaRequired && (
                <div className="flex w-full justify-center py-0.5">
                  <Turnstile
                    ref={turnstileRef}
                    siteKey={ENV.TURNSTILE_SITE_KEY}
                    options={{ size: 'flexible' }}
                    onSuccess={(token) => setCaptchaToken(token)}
                    onExpire={() => {
                      setCaptchaToken(null);
                      turnstileRef.current?.reset();
                    }}
                  />
                </div>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-lg bg-orange-600 py-2.5 text-sm font-semibold text-white shadow hover:bg-orange-700 disabled:opacity-60 transition-colors"
              >
                {submitting ? 'Signing in…' : 'Sign in'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/forgot-password')}
                className="w-full text-sm font-medium text-orange-700 hover:underline"
              >
                Forgot Password?
              </button>
            </form>
          ) : (
            <form onSubmit={otpForm.handleSubmit(onOtpSubmit)} className="space-y-5">
              <div className="flex flex-col items-center gap-2 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 text-orange-700">
                  <Shield className="h-6 w-6" />
                </div>
                {maskedDestination ? (
                  <p className="text-sm text-gray-600">Code sent to {maskedDestination}</p>
                ) : null}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Verification code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  className="w-full rounded-lg border border-gray-200 py-2.5 px-3 text-center text-lg tracking-widest focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
                  placeholder="000000"
                  {...otpForm.register('otp', {
                    required: 'Code is required',
                    minLength: { value: 6, message: 'Enter 6 digits' },
                    maxLength: { value: 6, message: 'Enter 6 digits' },
                  })}
                />
                {otpForm.formState.errors.otp && (
                  <p className="mt-1 text-sm text-red-600">{otpForm.formState.errors.otp.message}</p>
                )}
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full rounded-lg bg-orange-600 py-2.5 text-sm font-semibold text-white shadow hover:bg-orange-700 disabled:opacity-60"
              >
                {submitting ? 'Verifying…' : 'Verify and sign in'}
              </button>
              <button
                type="button"
                onClick={handleBackToCredentials}
                className="flex w-full items-center justify-center gap-1 text-sm font-medium text-orange-700 hover:underline"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to sign in
              </button>
            </form>
          )}
        </div>
        <p className="text-center text-xs text-gray-500">
          Kiosk Gaming CDN • JWT session • No shared cookies with Admin
        </p>
      </div>
    </div>
  );
}
